import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { Formik, FormikProps, FormikState } from "formik";
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/core/components/ui/button";
import { Checkbox } from "@/core/components/ui/checkbox";
import { Stack } from "@/core/components/ui/stack/stack";
import { Typography } from "@/core/components/ui/typography";
import { SERVICE_TITAN_STATUSES } from "@/business/constants";
import BusinessInfoPair from "@/business-settings/business/business-info-pair";
import {
  useServiceTitanIntegration,
  ServiceTitanFormValues,
} from "./useServiceTitanIntegration";

interface BusinessServiceTitanSettingsProps {
  isEditMode: boolean;
  setIsEditMode: (isEditMode: boolean) => void;
}

const BusinessServiceTitanSettings = ({
  isEditMode,
  setIsEditMode,
}: BusinessServiceTitanSettingsProps) => {
  const formikRef = useRef<FormikProps<ServiceTitanFormValues>>(null);
  const { businessId } = useParams();
  const [enableIntegration, setEnableIntegration] = useState(true);

  const {
    status,
    config,
    initialValues,
    validationSchema,
    isPending: isUpsertingCredentials,
    handleSubmit: handleSubmitCredentials,
    showTenantIdField,
    showClientFields,
    showErrorState,
    isSubmitDisabled,
  } = useServiceTitanIntegration({ businessId });

  useEffect(() => {
    formikRef.current?.resetForm({ values: initialValues });
    setEnableIntegration(true);
  }, [isEditMode, initialValues]);

  const handleSubmit = async (
    values: ServiceTitanFormValues,
    {
      setSubmitting,
      resetForm,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      resetForm: (values: Partial<FormikState<ServiceTitanFormValues>>) => void;
    },
  ) => {
    const onSuccess = (resp: { data?: { credentials_status?: string } }) => {
      const nextStatus = resp.data?.credentials_status;
      const isComplete =
        nextStatus === SERVICE_TITAN_STATUSES.connection_established;

      if (isComplete) {
        resetForm({ values });
        setIsEditMode(false);
      } else {
        resetForm({ values });
      }
    };

    try {
      handleSubmitCredentials(values);
      onSuccess({ data: { credentials_status: status } });
    } catch (error) {
      console.error("Error updating Service Titan credentials:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="md" fullWidth>
      <Typography variant="subtitleW2">Service Titan integration</Typography>
      <Stack gap="md" fullWidth>
        {showErrorState ? (
          <Stack
            gap="md"
            align="start"
            justify="center"
            direction="row"
            fullWidth
            className="max-w-lg rounded-lg border border-stroke-negative bg-red-50 p-4"
            role="alert"
            aria-live="assertive"
          >
            <ExclamationTriangleIcon className="h-6 w-12 text-negative-text" />
            <Typography variant="bodyW1">
              We were unable to connect to your Service Titan. Please check that
              all values are correct and submit again.
            </Typography>
          </Stack>
        ) : (
          !!config?.subtitle && (
            <Stack fullWidth>
              <Typography variant="bodyW2">{config?.subtitle}</Typography>
            </Stack>
          )
        )}
        {!!status && (
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
            fullWidth
          >
            {({
              values,
              handleChange,
              dirty,
              isValid,
              isSubmitting,
              setFieldValue,
              setSubmitting,
              resetForm,
            }) => (
              <>
                {(showTenantIdField ||
                  showClientFields ||
                  (config?.showSubmitButton && isEditMode)) && (
                  <Stack
                    direction="column"
                    gap="lg"
                    align="start"
                    fullWidth
                    className="2xl:flex-row 2xl:items-center"
                  >
                    {showTenantIdField && (
                      <BusinessInfoPair
                        isEditMode={isEditMode}
                        label="Tenant ID"
                        value={values.tenant_id}
                        onChange={handleChange}
                        name="tenant_id"
                        placeholder="Enter tenant ID"
                      />
                    )}
                    {showClientFields && (
                      <>
                        <BusinessInfoPair
                          isEditMode={isEditMode}
                          label="Client ID"
                          value={values.client_id}
                          onChange={handleChange}
                          name="client_id"
                          placeholder="Enter client ID"
                        />
                        <BusinessInfoPair
                          isEditMode={isEditMode}
                          label="Client Secret"
                          value={values.client_secret}
                          onChange={handleChange}
                          name="client_secret"
                          placeholder="Enter client secret"
                          className="size-1/3"
                        />
                      </>
                    )}
                    {config?.showSubmitButton && isEditMode && (
                      <Button
                        size="sm"
                        type="button"
                        onClick={() => {
                          handleSubmit(values, { setSubmitting, resetForm });
                        }}
                        disabled={
                          isSubmitDisabled(values) || !dirty || !isValid
                        }
                        loading={isUpsertingCredentials || isSubmitting}
                      >
                        Submit
                      </Button>
                    )}
                  </Stack>
                )}

                {status === SERVICE_TITAN_STATUSES.tenant_id_received && (
                  <Stack fullWidth>
                    <Typography variant="bodyW1" textColor="tertiary">
                      We received your Tenant ID. An email with next steps will
                      be sent soon.
                    </Typography>
                  </Stack>
                )}

                {isUpsertingCredentials && (
                  <Stack direction="row" gap="sm" align="center">
                    <ArrowPathIcon className="h-4 w-4" />
                    <Typography variant="bodyW1" textColor="tertiary">
                      Connecting to Service Titan...
                    </Typography>
                  </Stack>
                )}

                {status === SERVICE_TITAN_STATUSES.connection_established &&
                  isEditMode && (
                    <Stack direction="row" gap="sm" align="center">
                      <Checkbox
                        checked={enableIntegration}
                        onCheckedChange={() => {
                          setEnableIntegration(!enableIntegration);
                          setFieldValue("client_id", "");
                          setFieldValue("client_secret", "");
                        }}
                      />
                      <Typography variant="bodyW1">
                        Integration enabled
                      </Typography>
                    </Stack>
                  )}
              </>
            )}
          </Formik>
        )}
      </Stack>
    </Stack>
  );
};

export default BusinessServiceTitanSettings;
